import React, { useEffect } from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    Button,
    Icon,
    Flex,
    Image,
    Avatar,
    SimpleGrid,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { ChevronLeftIcon } from "@chakra-ui/icons";
import { FaUser, FaCalendarAlt, FaHospital, FaUserMd, FaBirthdayCake, FaPhone, FaEnvelope } from "react-icons/fa";

const MemberProfile = () => {
    const navigate = useNavigate();

    const profileData = {
        name: "John Smith",
        age: 58,
        email: "john.smith@email.com",
        phone: "+61 412 345 678",
        surgeryType: "Hip Replacement",
        surgeryDate: "February 15, 2026",
        surgeon: "Dr. Sarah Chen",
        hospital: "Royal Melbourne Hospital",
        memberSince: "January 2026",
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const DetailCard = ({ icon, label, value }: { icon: any; label: string; value: string }) => (
        <Box
            bg="whiteAlpha.50"
            borderRadius="xl"
            p={5}
            border="1px solid"
            borderColor="whiteAlpha.100"
            transition="all 0.2s"
            _hover={{ borderColor: "whiteAlpha.200" }}
        >
            <HStack spacing={4}>
                <Box
                    bg="whiteAlpha.100"
                    borderRadius="lg"
                    p={3}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Icon as={icon} w={5} h={5} color="#4A90E2" />
                </Box>
                <VStack align="flex-start" spacing={0}>
                    <Text
                        fontSize="xs"
                        fontWeight="500"
                        letterSpacing="0.1em"
                        textTransform="uppercase"
                        color="whiteAlpha.500"
                    >
                        {label}
                    </Text>
                    <Text fontSize="lg" fontWeight="400" color="white">
                        {value}
                    </Text>
                </VStack>
            </HStack>
        </Box>
    );

    return (
        <Box bg="#000000" minHeight="100vh" color="white">
            <Box 
                bg="rgba(0, 0, 0, 0.9)"
                borderBottom="1px solid"
                borderColor="whiteAlpha.100"
                position="sticky"
                top={0}
                zIndex={100}
                backdropFilter="blur(20px)"
            >
                <Container maxW="7xl" px={{ base: 4, md: 6 }}>
                    <Flex 
                        justify="space-between" 
                        align="center" 
                        minH={{ base: "44px", md: "48px" }}
                    >
                        <HStack spacing={2} cursor="pointer" onClick={() => navigate('/')}>
                            <Image 
                                src="/surgready/logo.png" 
                                alt="SurgReady" 
                                h={{ base: "44px", md: "52px" }}
                                objectFit="contain"
                            />
                        </HStack>
                        <HStack spacing={{ base: 4, md: 8 }}>
                            <Button
                                variant="ghost"
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="whiteAlpha.900"
                                onClick={() => navigate('/all-modules')}
                                _hover={{ color: "#4A90E2", bg: "transparent" }}
                            >
                                All Modules
                            </Button>
                            <Button
                                variant="ghost"
                                fontSize="xs"
                                fontWeight="400"
                                letterSpacing="0.1em"
                                textTransform="uppercase"
                                color="whiteAlpha.900"
                                _hover={{ color: "#4A90E2", bg: "transparent" }}
                            >
                                Community
                            </Button>
                            <HStack
                                spacing={2}
                                opacity={0.7}
                            >
                                <Text
                                    fontSize="xs"
                                    fontWeight="400"
                                    letterSpacing="0.1em"
                                    textTransform="uppercase"
                                    color="whiteAlpha.900"
                                    display={{ base: "none", md: "block" }}
                                >
                                    I am SurgReady
                                </Text>
                                <Box
                                    bg="#4A90E2"
                                    borderRadius="full"
                                    w={8}
                                    h={8}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                >
                                    <Icon as={FaUser} w={4} h={4} color="white" />
                                </Box>
                            </HStack>
                        </HStack>
                    </Flex>
                </Container>
            </Box>

            <Container maxW="900px" px={{ base: 4, md: 6 }} py={{ base: 8, md: 16 }}>
                <VStack spacing={10} align="stretch">
                    <Button
                        variant="ghost"
                        size="sm"
                        color="whiteAlpha.600"
                        _hover={{ color: "#4A90E2" }}
                        onClick={() => navigate('/member-homepage')}
                        px={0}
                        alignSelf="flex-start"
                    >
                        <ChevronLeftIcon w={5} h={5} mr={1} />
                        Back to Dashboard
                    </Button>

                    <VStack spacing={6} align="center">
                        <Avatar
                            size="2xl"
                            name={profileData.name}
                            bg="#4A90E2"
                            color="white"
                            fontSize="3xl"
                        />
                        <VStack spacing={1}>
                            <Heading
                                fontSize={{ base: "3xl", md: "4xl" }}
                                fontWeight="400"
                                letterSpacing="-0.02em"
                            >
                                {profileData.name}
                            </Heading>
                            <Text
                                fontSize="sm"
                                color="whiteAlpha.600"
                                letterSpacing="0.05em"
                                textTransform="uppercase"
                            >
                                SurgReady Member since {profileData.memberSince}
                            </Text>
                        </VStack>
                    </VStack>

                    <Box
                        bg="whiteAlpha.50"
                        borderRadius="2xl"
                        p={6}
                        border="1px solid"
                        borderColor="#4A90E2"
                    >
                        <VStack spacing={4} align="center">
                            <Text
                                fontSize="xs"
                                fontWeight="500"
                                letterSpacing="0.15em"
                                textTransform="uppercase"
                                color="#4A90E2"
                            >
                                Upcoming Surgery
                            </Text>
                            <Heading
                                fontSize={{ base: "2xl", md: "3xl" }}
                                fontWeight="400"
                                letterSpacing="-0.02em"
                                textAlign="center"
                            >
                                {profileData.surgeryType}
                            </Heading>
                            <Text fontSize="lg" color="whiteAlpha.700">
                                {profileData.surgeryDate}
                            </Text>
                        </VStack>
                    </Box>

                    <VStack spacing={4} align="stretch">
                        <Heading
                            fontSize="xl"
                            fontWeight="400"
                            letterSpacing="-0.01em"
                            color="whiteAlpha.800"
                        >
                            Personal Details
                        </Heading>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <DetailCard icon={FaBirthdayCake} label="Age" value={`${profileData.age} years old`} />
                            <DetailCard icon={FaPhone} label="Phone" value={profileData.phone} />
                            <DetailCard icon={FaEnvelope} label="Email" value={profileData.email} />
                            <DetailCard icon={FaUserMd} label="Surgeon" value={profileData.surgeon} />
                            <DetailCard icon={FaHospital} label="Hospital" value={profileData.hospital} />
                            <DetailCard icon={FaCalendarAlt} label="Surgery Date" value={profileData.surgeryDate} />
                        </SimpleGrid>
                    </VStack>

                    <Button
                        variant="outline"
                        size="lg"
                        borderColor="whiteAlpha.300"
                        color="whiteAlpha.800"
                        borderRadius="full"
                        fontWeight="400"
                        letterSpacing="0.05em"
                        _hover={{
                            borderColor: "#4A90E2",
                            color: "#4A90E2",
                        }}
                        alignSelf="center"
                        mt={4}
                    >
                        Edit My Details
                    </Button>
                </VStack>
            </Container>
        </Box>
    );
};

export default MemberProfile;
